import { default as React, useState, useCallback } from 'react';
import { BaseCardProps } from '../../shared/shared';
import { SignupFormDataProvider } from './SignupFormDataContext';
import { ensureUnreachable } from '@signup/shared/src/utils/ensure';
import {
  SignupFormControllerProvider,
  useSignupFormController,
} from './SignupFormControllerContext';
import { CortexCodeWaitListCard } from './CortexCodeWaitListCard';
import { CortexWaitlistStatus } from './CortexWaitlistStatus';

enum SignupCardStage {
  WaitlistForm = 'WaitlistForm',
  EmailVerification = 'EmailVerification',
}

enum SignupCardStep {
  WaitlistFormOne = 'WaitlistFormOne',
  SendingEmail = 'SendingEmail',
  Completed = 'Completed',
}

type SignupCardState =
  | {
      stage: SignupCardStage.WaitlistForm;
      step: SignupCardStep.WaitlistFormOne;
    }
  | {
      stage: SignupCardStage.EmailVerification;
      step: SignupCardStep.SendingEmail | SignupCardStep.Completed;
    };

function useNavigationControl() {
  const [state, setState] = useState<SignupCardState>({
    stage: SignupCardStage.WaitlistForm,
    step: SignupCardStep.WaitlistFormOne,
  });

  const continueNext = useCallback(() => {
    switch (state.stage) {
      case SignupCardStage.WaitlistForm:
        switch (state.step) {
          case SignupCardStep.WaitlistFormOne:
            return setState({
              stage: SignupCardStage.EmailVerification,
              step: SignupCardStep.SendingEmail,
            });
          default:
            ensureUnreachable(state);
        }
      case SignupCardStage.EmailVerification:
        switch (state.step) {
          case SignupCardStep.SendingEmail:
            return setState({
              stage: SignupCardStage.EmailVerification,
              step: SignupCardStep.Completed,
            });
          case SignupCardStep.Completed:
            return null;
          default:
            ensureUnreachable(state);
        }
      default:
        ensureUnreachable(state);
    }
  }, [state]);

  const restartWithoutClearInputs = useCallback(() => {
    setState({
      stage: SignupCardStage.WaitlistForm,
      step: SignupCardStep.WaitlistFormOne,
    });
  }, [setState]);

  return {
    state,
    setState,
    continueNext,
    restartWithoutClearInputs,
  };
}

const CortexCodeWaitListCardWrapperImpl = () => {
  const { state, continueNext, restartWithoutClearInputs } = useNavigationControl();
  const { clearWaitlistErrorAndResetRecaptcha } = useSignupFormController();
  const [fadeOut, setFadeOut] = useState(false);

  // Apply a 250ms fade to any action
  const withTransition = useCallback((action: () => void) => {
    setFadeOut(true);
    setTimeout(() => {
      action();
      setFadeOut(false);
    }, 250);
  }, []);

  const handleContinueNext = useCallback(() => {
    withTransition(() => continueNext());
  }, [continueNext, withTransition]);

  const handleTryAgain = useCallback(() => {
    withTransition(() => {
      restartWithoutClearInputs();
      clearWaitlistErrorAndResetRecaptcha();
    });
  }, [restartWithoutClearInputs, clearWaitlistErrorAndResetRecaptcha, withTransition]);

  const render = useCallback(() => {
    switch (state.stage) {
      case SignupCardStage.WaitlistForm:
        switch (state.step) {
          case SignupCardStep.WaitlistFormOne:
            return <CortexCodeWaitListCard continueNext={handleContinueNext} />;
          default:
            ensureUnreachable(state);
        }
      case SignupCardStage.EmailVerification:
        switch (state.step) {
          case SignupCardStep.SendingEmail:
            return <CortexWaitlistStatus onTryAgain={handleTryAgain} />;
          case SignupCardStep.Completed:
            return <CortexWaitlistStatus onTryAgain={handleTryAgain} />;
          default:
            ensureUnreachable(state);
        }
      default:
        ensureUnreachable(state);
    }
  }, [state, handleContinueNext, handleTryAgain]);

  return (
    <div
      style={{
        opacity: fadeOut ? 0 : 1,
        transition: 'opacity 0.25s ease-in-out',
      }}
    >
      {render()}
    </div>
  );
};

export const CortexCodeWaitListCardWrapper = (props: BaseCardProps) => {
  return (
    <SignupFormDataProvider>
      <SignupFormControllerProvider {...props}>
        <CortexCodeWaitListCardWrapperImpl />
      </SignupFormControllerProvider>
    </SignupFormDataProvider>
  );
};
